<template>
    <base-dropdown
        ref="dropdownRef"
        menu-on-right
        class="nav-item"
        tag="li"
        title-tag="a"
        title-classes="nav-link pr-0"
        @change="onDropdownToggle"
    >
        <span slot="title-container" class="d-flex">
            <span v-if="activeCompany.text" class="h4 text-white pr-1 mr-2 mb-0">{{
                activeCompany.text
            }}</span>
            <b-icon-eye></b-icon-eye>
        </span>

        <template v-if="companiesList.length">
            <li class="company-dropdown__search-wrapper" @click.stop>
                <div class="company-dropdown__search-container">
                    <b-form-input
                        ref="searchInputRef"
                        v-model="searchQuery"
                        placeholder="Search companies…"
                        size="sm"
                        autocomplete="off"
                        class="company-dropdown__search-input"
                        @keydown="onSearchKeydown"
                    />
                    <button
                        v-if="searchQuery"
                        type="button"
                        class="company-dropdown__clear-btn"
                        @click.prevent.stop="clearSearch"
                        aria-label="Clear search"
                    >
                        ✕
                    </button>
                </div>
            </li>

            <template v-if="filteredList.length">
                <b-dropdown-item
                    v-for="company in filteredList"
                    :key="`company-list-item-${company.value}`"
                    :to="{
                        name: $route.name,
                        params: getRouteParams(company.value),
                    }"
                    :disabled="activeCompany.value === company.value"
                >
                    <span>
                        <template v-for="(part, index) in getHighlightedParts(company.text)">
                            <mark
                                v-if="part.matched"
                                :key="`m-${index}`"
                                class="company-dropdown__highlight"
                                >{{ part.text }}</mark
                            ><span v-else :key="`s-${index}`">{{ part.text }}</span>
                        </template>
                    </span>
                </b-dropdown-item>
            </template>

            <li v-else class="company-dropdown__empty-state">
                <small>No companies match your search</small>
            </li>
        </template>
        <small v-else class="px-3">No items found.</small>
    </base-dropdown>
</template>

<script>
import { userMixin, reportMixin } from "@mixins";
import { CompaniesService } from "@services";

export default {
    props: {
        activeReportId: Number,
    },
    mixins: [userMixin, reportMixin],
    data: () => ({
        companies: [],
        searchQuery: "",
    }),
    computed: {
        filteredList() {
            if (!this.searchQuery) return this.companiesList;

            const query = this.searchQuery.toLowerCase();
            return this.companiesList.filter((company) =>
                company.text.toLowerCase().includes(query)
            );
        },
        companiesList() {
            let list = this.companies;

            if (this.activeReportId)
                list = list.filter((item) => {
                    if (!this.activeReportId) return true;

                    const assignedReports = item.reports.map(
                        (r_item) => r_item.report_id
                    );

                    return assignedReports.includes(this.activeReportId);
                });

            return list.map((item) => ({
                value: item.id,
                text: item.name,
            }));
        },
        activeCompany() {
            const index = this.companiesList.findIndex(
                (item) => item.value === this.$route.query.company
            );

            if (0 <= index) return this.companiesList[index];

            return {
                value: null,
                text: null,
            };
        },
    },
    methods: {
        async fetchCompanies() {
            if (!this.isUserRole("Superuser")) return;

            if (!["Dashboard", "ReportView"].includes(this.$route.name)) return;

            const res = await CompaniesService.list(
                {
                    page_size: 1000,
                },
                false
            );

            this.companies = res.data.data.results;
        },
        getRouteParams(companyId = null) {
            return "Dashboard" === this.$route.name
                ? {
                      report_id: this.encodeReportID(this.activeReportId, companyId),
                  }
                : {
                      id: this.encodeReportID(this.activeReportId, companyId),
                  };
        },
        onDropdownToggle(isOpen) {
            if (!isOpen) {
                this.searchQuery = "";
                return;
            }

            this.searchQuery = "";
            this.$nextTick(() => {
                if (this.$refs.searchInputRef) {
                    this.$refs.searchInputRef.focus();
                }
            });
        },
        onSearchKeydown(event) {
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                if (this.searchQuery) {
                    this.searchQuery = "";
                } else if (this.$refs.dropdownRef) {
                    this.$refs.dropdownRef.closeDropDown();
                }
            }
        },
        clearSearch() {
            this.searchQuery = "";
            this.$nextTick(() => {
                if (this.$refs.searchInputRef) {
                    this.$refs.searchInputRef.focus();
                }
            });
        },
        getHighlightedParts(name) {
            if (!this.searchQuery) return [{ text: name, matched: false }];

            const query = this.searchQuery.toLowerCase();
            const nameLower = name.toLowerCase();
            const parts = [];
            let lastIndex = 0;
            let matchIndex = nameLower.indexOf(query);

            while (-1 !== matchIndex) {
                if (matchIndex > lastIndex) {
                    parts.push({ text: name.slice(lastIndex, matchIndex), matched: false });
                }
                parts.push({
                    text: name.slice(matchIndex, matchIndex + query.length),
                    matched: true,
                });
                lastIndex = matchIndex + query.length;
                matchIndex = nameLower.indexOf(query, lastIndex);
            }

            if (lastIndex < name.length) {
                parts.push({ text: name.slice(lastIndex), matched: false });
            }

            return parts;
        },
    },
    watch: {
        $route: "fetchCompanies",
    },
    mounted() {
        this.fetchCompanies();
    },
};
</script>

<style lang="scss" scoped>
.company-dropdown {
    &__search-wrapper {
        padding: 0.5rem 0.75rem 0.375rem;
        list-style: none;
    }

    &__search-container {
        position: relative;
        display: flex;
        align-items: center;
    }

    &__search-input {
        padding-right: 1.75rem !important;
    }

    &__clear-btn {
        position: absolute;
        right: 0.375rem;
        background: none;
        border: none;
        padding: 0;
        line-height: 1;
        font-size: 0.75rem;
        color: #6c757d;
        cursor: pointer;

        &:hover {
            color: #343a40;
        }
    }

    &__highlight {
        background-color: rgba(255, 193, 7, 0.4);
        padding: 0;
        border-radius: 2px;
    }

    &__empty-state {
        list-style: none;
        padding: 0.5rem 1rem;
        color: #6c757d;
    }
}
</style>
